import {BigId} from '@colonist/types'
import {MAX_UNREAD_NOTIFICATION_COUNT, NotificationData, NotificationTypeEnum} from '../../../../shared/NotificationTypes'
import {UINotificationItemView} from './UINotificationItemView'
import {UINotificationSendHelper} from './UINotificationSendHelper'
import {CTANotificationData, UICTANotificationView} from './UICTANotificationView'
import {UINotificationItemViewBase} from './UINotificationItemViewBase'
import {ClientUserState} from '../ClientUserState'
import {UIUtilsNotifications} from './UIUtilsNotifications'
import {UIUtilsFriends} from '../ui_friends/UIUtilsFriends'
import {setElementOnClick} from '../ui_utils/DomUtils'

export class UINotificationPanelController {
    private readonly notificationIcon: HTMLElement
    private readonly notificationContainer: HTMLElement
    private readonly notificationPanel: HTMLElement
    readonly notificationPanelContent: HTMLElement
    private readonly notifications: UINotificationItemViewBase[] = []
    private readonly notificationIconUnreadNumber: HTMLElement
    private readonly notificationIconUnreadContainer: HTMLElement
    private readonly markAllAsReadButton: HTMLElement
    private readonly noNotificationsMessage: HTMLElement

    private readonly lazyLoadDOMTolerance = 1

    private lastUnreadNotificationCount: number = 0
    private timeOfLastNotificationRequest: Date = new Date()
    private readonly LAZY_LOAD_THROTTLE_MS: number = 500
    private lazyLoadingDisabled: boolean = false

    private readonly UPDATE_NOTIFICATION_TIMESTAMP_MS = 5000
    private readonly userNameNotificationMap: Map<string, UICTANotificationView> = new Map()
    private readonly cachedFriendRequests: string[] = []

    private readonly addNotificationCallback: () => void

    constructor(addNotificationCallback: () => void) {
        this.addNotificationCallback = addNotificationCallback
        this.notificationIcon = document.getElementById('header_profile_notification_icon') as HTMLElement
        this.notificationContainer = document.getElementById('header_profile_notification_container') as HTMLElement
        this.notificationPanel = document.getElementById('header_profile_notification_panel') as HTMLElement
        this.notificationPanelContent = document.getElementById('header_profile_notification_panel_content') as HTMLElement
        this.notificationIconUnreadNumber = document.getElementById('header_profile_notification_count') as HTMLElement
        this.notificationIconUnreadContainer = document.getElementById('header_profile_notification_count_container') as HTMLElement
        this.markAllAsReadButton = document.getElementById('header_profile_btn_mark_all_as_read') as HTMLElement
        this.noNotificationsMessage = document.getElementById('header_profile_notification_no_notifications') as HTMLElement

        ClientUserState.registerChangeListener(() => {
            if(UIUtilsNotifications.canUseLiveNotificationService()) this.showNotificationContainer()
        })

        setElementOnClick(this.notificationIcon, () => this.onNotificationIconClick())
        setElementOnClick(this.markAllAsReadButton, () => this.sendMarkAllNotificationsAsRead())
        document.addEventListener('pointerdown', (event: PointerEvent) => this.hidePanelIfClickedOutside(event))

        setInterval(() => this.updateNotificationTimestamps(), this.UPDATE_NOTIFICATION_TIMESTAMP_MS)

        this.setLazyLoadAction()

        const notificationPanelHeaderBackButton = document.getElementById('header_profile_notification_panel_header_back_button') as HTMLElement
        if(notificationPanelHeaderBackButton != undefined) setElementOnClick(notificationPanelHeaderBackButton, () => this.hideNotificationPanel())
        UIUtilsFriends.setCallbackFriendRequestResponded(async(username, isAccepted) => {
            const ctaNotification = this.userNameNotificationMap.get(username)
            if(ctaNotification == undefined) return
            this.removeCTANotification(ctaNotification)
        })
    }

    private setLazyLoadAction() {
        this.notificationPanelContent.addEventListener('scroll', (e) => {
            if(this.lazyLoadingDisabled) return
            const isBottomReached = (this.notificationPanelContent.scrollTop + this.notificationPanelContent.clientHeight) >= (this.notificationPanelContent.scrollHeight - this.lazyLoadDOMTolerance)
            if(isBottomReached == false) return
            this.getOlderNotificationsThrottled()
        })
    }

    private getOlderNotificationsThrottled() {
        const now = new Date()
        const timeSinceLastRequest = now.getTime() - this.timeOfLastNotificationRequest.getTime()
        if(timeSinceLastRequest < this.LAZY_LOAD_THROTTLE_MS) return

        const lastNotification = this.notifications[this.notifications.length - 1]
        UINotificationSendHelper.getNotificationsAfterDate(lastNotification.dateReceived)
        this.timeOfLastNotificationRequest = now
    }

    hideNotificationPanel() {
        this.notificationPanel.classList.add('hidden')
    }

    private showNotificationContainer() {
        this.notificationContainer.classList.remove('hidden')
    }

    private onNotificationIconClick() {
        this.notificationPanel.classList.toggle('hidden')
    }

    private hidePanelIfClickedOutside(event: PointerEvent) {
        const target = event.target as HTMLElement
        const targetInsidePanel = this.notificationPanel.contains(target)
        const targetInsideIcon = this.notificationIcon.contains(target)
        if(targetInsidePanel) return
        if(targetInsideIcon) return
        this.hideNotificationPanel()
    }

    private addNotification(notificationData: NotificationData) {
        const notificationIdx = this.findNotificationById(notificationData.id)
        if(notificationIdx > -1) return
        const notificationItem = new UINotificationItemView(notificationData, this.notificationPanelContent)
        notificationItem.setCallbackOnClick(() => this.removeCTANotification(notificationItem))
        notificationItem.setOnClose(() => this.hideNotificationPanel())
        this.notificationPanelContent.appendChild(notificationItem.container)
        this.notifications.push(notificationItem)
        this.sortNotificationViewsByDateReceived()
    }

    private sortNotificationViewsByDateReceived() {
        this.notifications.sort((a, b) => b.dateReceived.getTime() - a.dateReceived.getTime())
        for(const notification of this.notifications) this.notificationPanelContent.removeChild(notification.container)
        for(const notification of this.notifications) this.notificationPanelContent.appendChild(notification.container)
    }

    private sendMarkAllNotificationsAsRead() {
        const unreadNotificationsExist = this.notifications.some((notificationView) => !notificationView.isRead)
        if(unreadNotificationsExist == false) return
        UINotificationSendHelper.sendMarkAllNotificationsAsRead()
    }

    addNotifications(notifications: NotificationData[]) {
        if(notifications.length == 0) this.lazyLoadingDisabled = true
        for(const notificationData of notifications) this.addNotification(notificationData)
        this.noNotificationsMessage.classList.toggle('hidden', this.notifications.length > 0)

        this.addNotificationCallback()
    }

    markNotificationAsRead(notificationId: BigId) {
        const notification = this.notifications.find((notificationView) => notificationView.notificationId == notificationId)
        notification?.removeUnreadIcon()
    }

    markAllNotificationsAsRead() {
        for(const notification of this.notifications) notification.removeUnreadIcon()
    }

    displayNumberOfUnreadNotifications(unreadNotificationCount: number) {
        const countOfNotificationsWithCTAs = this.notifications.filter((notification) => notification.hasCTAs).length
        unreadNotificationCount = unreadNotificationCount + countOfNotificationsWithCTAs

        const shouldCapUnreadNotificationCount = unreadNotificationCount > MAX_UNREAD_NOTIFICATION_COUNT - 1
        const unreadNotificationCountToDisplay = shouldCapUnreadNotificationCount ? `${MAX_UNREAD_NOTIFICATION_COUNT - 1}+` : unreadNotificationCount.toString()
        this.notificationIconUnreadNumber.innerText = unreadNotificationCountToDisplay
        this.notificationIconUnreadContainer.classList.toggle('hidden', unreadNotificationCount == 0)

        this.lastUnreadNotificationCount = unreadNotificationCount - countOfNotificationsWithCTAs
    }

    addCTANotification(notificationData: CTANotificationData): UICTANotificationView {
        const newCTANotification = new UICTANotificationView(notificationData, this.notificationPanelContent)
        newCTANotification.setCallbackOnClick(() => this.removeCTANotification(newCTANotification))
        this.notificationPanelContent.appendChild(newCTANotification.container)
        this.notifications.push(newCTANotification)
        this.sortNotificationViewsByDateReceived()
        this.displayNumberOfUnreadNotifications(this.lastUnreadNotificationCount)
        this.noNotificationsMessage.classList.toggle('hidden', this.notifications.length > 0)
        return newCTANotification
    }

    removeCTANotification(notificationView: UICTANotificationView) {
        const index = this.notifications.indexOf(notificationView)
        if(index == -1) return
        this.notifications.splice(index, 1)
        this.notificationPanelContent.removeChild(notificationView.container)
        this.displayNumberOfUnreadNotifications(this.lastUnreadNotificationCount)
        notificationView.container.remove()
        this.noNotificationsMessage.classList.toggle('hidden', this.notifications.length > 0)
    }

    private updateNotificationTimestamps() {
        for(const notification of this.notifications) notification.updateTimestamp()
    }

    private findNotificationById(id: BigId): number {
        return this.notifications.findIndex((notification) => notification.notificationId == id)
    }

    addFriendRequestNotification(username: string, dateCreated: Date) {
        if(this.cachedFriendRequests.includes(username)) return
        this.cachedFriendRequests.push(username)
        const notificationData: CTANotificationData = {
            dateReceived: dateCreated,
            typeData: {
                type: NotificationTypeEnum.FriendRequestReceived,
                username: username,
            },
        }
        const ctaNotification = this.addCTANotification(notificationData)
        this.userNameNotificationMap.set(username, ctaNotification)
    }
}
